import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TextField } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { GeoRehandleTruckingTruckDTO } from "../../../DTOs/Geology/GeoRehandleTruckingTruckDTO";
import { ShowAllAutocomplete } from "../../../components/ShowAllAutocomplete/ShowAllAutocomplete";
import { TRUCK_LABELS } from "../../../components/ShowAllAutocomplete/showAllLabels";

type TruckSelectProps = {
  truckPlantId?: number
  trucks: GeoRehandleTruckingTruckDTO[]
  isLoading: boolean
  disabled: boolean
  onChange: (truck: GeoRehandleTruckingTruckDTO) => unknown
  /** The row's machine is a loader, which may carry its own load instead of filling a truck. */
  allowOwnLoad?: boolean
  onOwnLoad?: () => unknown
}

const TruckSelect: React.FC<TruckSelectProps> = ({
  truckPlantId,
  trucks,
  isLoading,
  disabled,
  onChange,
  allowOwnLoad = false,
  onOwnLoad
}) => {
  const { t } = useTranslation("geology");
  const [isWorking, setIsWorking] = useState(false);
  const selected = trucks.find(x => x.plantId === truckPlantId) ?? null;
  const showsOwnLoad = allowOwnLoad && selected == null;

  return (
    <ShowAllAutocomplete
      labels={TRUCK_LABELS}
      classes={{ root: 'w-full' }}
      size="small"
      loading={isLoading || isWorking}
      disabled={isLoading || isWorking || disabled}
      disableClearable={!allowOwnLoad}
      options={trucks}
      getOptionLabel={(option) => option.plantRef}
      value={selected}
      isOptionEqualToValue={(option, value) => option.plantId === value.plantId}
      onChange={async (_event, newValue) => {
        const save = newValue ? () => onChange(newValue) : onOwnLoad;
        if (!save) return;
        setIsWorking(true);
        await save();
        setIsWorking(false);
      }}
      renderInput={(params) => <TextField
        {...params}
        label={t("fields.truck")}
        placeholder={showsOwnLoad ? t("movement.ownLoad") : undefined}
        slotProps={{
          ...params.slotProps,
          inputLabel: showsOwnLoad ? { ...params.slotProps.inputLabel, shrink: true } : params.slotProps.inputLabel,
          input: {
            ...params.slotProps.input,
            endAdornment: (
              <>
                {(isLoading || isWorking) && <FontAwesomeIcon icon={faSpinner} spin />}
                {params.slotProps.input.endAdornment}
              </>
            )
          }
        }}
      />}
    />
  );
};

export default TruckSelect;
